import type { ListItemButtonProps } from '@mui/material';
import {
  Box,
  ListItem,
  ListItemButton as MUIListItemButton,
  ListSubheader,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import isEqual from 'lodash.isequal';
import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ViewsRestrictions } from '@/entities/session';
import { ViewAccessType, ViewType } from '@/entities/session';
import { Button, Checkbox, Features, useFeatureFlags } from '@/shared/ui';

const ListItemButton = styled((props: ListItemButtonProps) => {
  return (
    <MUIListItemButton component="label" {...props}>
      <Checkbox
        color="primary"
        sx={{
          p: 0,
          '&.Mui-disabled .MuiSvgIcon-root': {
            color: (theme) => theme.palette.primary.main,
          },
        }}
        disabled
        checked={props.selected}
        tabIndex={-1}
        disableRipple
      />
      {props.children}
    </MUIListItemButton>
  );
})(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.25),
  lineHeight: '22px',
  fontSize: '14px',
  padding: `${theme.spacing(1.25)} ${theme.spacing(2)}`,
  color: 'var(--neutral-700)',

  '&.Mui-selected': {
    backgroundColor: 'transparent',
  },

  '&:hover, &:focus-visible': {
    backgroundColor: 'var(--accent-100)',
  },
}));

interface Props {
  value: ViewsRestrictions;
  onChange: (value: ViewsRestrictions) => void;
}

export function ViewsRestrictionsPanel({ value, onChange }: Props) {
  const [internalValue, setInternalValue] = useState<ViewsRestrictions>(value);
  const { isFeatureAvailable } = useFeatureFlags();
  const viewGroups = useMemo(() => {
    const analyticsGroup = [
      {
        name: 'Cohort Marketing Performance',
        value: ViewType.cohortMarketingPerformance,
      },
      {
        name: 'Calendar Insights',
        value: ViewType.calendarInsights,
      },
      {
        name: 'Cohort Chart',
        value: ViewType.cohortChart,
      },
      {
        name: 'Calendar Chart',
        value: ViewType.calendarChart,
      },
      {
        name: 'Cohort Scanner',
        value: ViewType.cohortScanner,
      },
      {
        name: 'Cohort Retention',
        value: ViewType.cohortRetention,
      },
      {
        name: 'ROI Breakdown',
        value: ViewType.roiBreakdown,
      },
    ];
    const administrationGroup = [
      {
        name: 'Integrations',
        value: ViewType.integrations,
      },
      ...(isFeatureAvailable(Features.productCatalog)
        ? [
            {
              name: 'Product Catalog',
              value: ViewType.productsCatalog,
            },
          ]
        : [
            {
              name: 'Web Links',
              value: ViewType.webLinks,
            },
          ]),
    ];

    return {
      Analytics: analyticsGroup,
      Administration: administrationGroup,
    };
  }, [isFeatureAvailable]);
  const hasUnsavedChanges = useMemo(() => {
    return !isEqual(value, internalValue);
  }, [value, internalValue]);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = (option: ViewType) => () => {
    const oldValue = internalValue[option];
    let newValue = ViewAccessType.ALLOWED;

    if (!oldValue || oldValue === ViewAccessType.ALLOWED) {
      newValue = ViewAccessType.DENIED;
    }

    setInternalValue({ ...internalValue, [option]: newValue });
  };
  const handleSubmitChange = () => {
    onChange(internalValue);
  };
  const handleResetChange = () => {
    setInternalValue(value);
  };

  return (
    <>
      <Box
        component="ul"
        sx={{
          flexShrink: 1,
          overflowY: 'auto',
          m: 0,
          p: 0,
          width: '100%',
        }}
      >
        {Object.entries(viewGroups).map(([group, options]) => {
          return (
            <Fragment key={group}>
              <ListSubheader
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                  fontWeight: 500,
                  color: '#343844',
                  backgroundColor: '#F1F3F8',
                }}
              >
                {group}
              </ListSubheader>
              {options.map((option) => (
                <ListItem key={option.value} disablePadding>
                  <ListItemButton
                    selected={
                      internalValue[option.value] == null ||
                      internalValue[option.value] === ViewAccessType.ALLOWED
                    }
                    onClick={handleChange(option.value)}
                  >
                    {option.name}
                  </ListItemButton>
                </ListItem>
              ))}
            </Fragment>
          );
        })}
      </Box>
      {hasUnsavedChanges && (
        <Box
          sx={{
            borderTop: '1px solid #DDDFE7',
            backgroundColor: 'white',
            p: 2,
            display: 'flex',
            gap: 1,
          }}
        >
          <Button onClick={handleSubmitChange}>Apply</Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetChange}
          >
            Reset
          </Button>
        </Box>
      )}
    </>
  );
}
